import { useState, useEffect } from 'react'
import { listFiles, getFileUrl } from '../services/api'
import { formatTime } from '../utils/time'
import { SearchBar, FilterBar, FilterPill, EmptyState } from '@ofself/shell'

function formatShortTime(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  const now = new Date()
  const diff = now - d
  if (diff < 86400000 && d.getDate() === now.getDate()) return formatTime(ts)
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  if (diff < 7 * 86400000) return days[d.getDay()]
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function getFileExt(filename) {
  if (!filename) return null
  const dot = filename.lastIndexOf('.')
  if (dot === -1) return null
  return filename.slice(dot + 1).toUpperCase()
}

export default function FilesPage({ selectedFile, onSelectFile, onJumpToMessage }) {
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState(null)

  useEffect(() => {
    setLoading(true)
    listFiles()
      .then(res => setFiles(res.files || []))
      .catch(err => console.error('Failed to fetch files:', err))
      .finally(() => setLoading(false))
  }, [])

  function formatSize(size) {
    if (size < 1024) return `${size} B`
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
    return `${(size / (1024 * 1024)).toFixed(1)} MB`
  }

  const extCounts = {}
  for (const f of files) {
    const ext = getFileExt(f.original_filename)
    if (ext) extCounts[ext] = (extCounts[ext] || 0) + 1
  }
  if (extCounts['JPG'] || extCounts['JPEG']) {
    extCounts['JPG'] = (extCounts['JPG'] || 0) + (extCounts['JPEG'] || 0)
    delete extCounts['JPEG']
  }
  const extTags = Object.keys(extCounts).sort((a, b) => extCounts[b] - extCounts[a])

  const filtered = (() => {
    let items = files
    if (filter !== 'all') {
      items = items.filter(f => {
        const ext = getFileExt(f.original_filename)
        if (filter === 'JPG') return ext === 'JPG' || ext === 'JPEG'
        return ext === filter
      })
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      items = items.filter(f =>
        (f.original_filename || '').toLowerCase().includes(q) ||
        (f.uploader_name || '').toLowerCase().includes(q)
      )
    }
    return items
  })()

  return (
    <div className="sidebar">
      <SearchBar
        value={search}
        onChange={e => setSearch(e.target.value)}
        onClear={() => setSearch('')}
        placeholder="Search files..."
      />

      <FilterBar>
        <FilterPill active={filter === 'all'} onClick={() => setFilter('all')}>All</FilterPill>
        {extTags.map(ext => (
          <FilterPill key={ext} active={filter === ext} onClick={() => setFilter(ext)}>{ext}</FilterPill>
        ))}
      </FilterBar>

      <ul className="block__body block-scroll list-rows" style={{ padding: '16px 0 0' }}>
        {loading ? (
          <EmptyState as="li">Loading files...</EmptyState>
        ) : filtered.length === 0 ? (
          <EmptyState as="li">{search ? 'No results found' : 'No files shared yet'}</EmptyState>
        ) : (
          filtered.map(file => {
            const isImage = file.mimetype?.startsWith('image/')
            const isVideo = file.mimetype?.startsWith('video/')
            const isAudio = file.mimetype?.startsWith('audio/')
            const ext = getFileExt(file.original_filename)
            const isExpanded = expandedId === file.id
            return (
              <li
                key={file.id}
                className={`ofself-row file-item${isExpanded ? ' file-item-expanded' : ''}${selectedFile?.id === file.id ? ' is-active' : ''}`}
                onClick={() => setExpandedId(isExpanded ? null : file.id)}
              >
                <div className="file-item-row">
                  <div className="activity-icon-wrap">
                    {isImage ? (
                      <img src={getFileUrl(file.id)} alt="" style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover' }} />
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    )}
                  </div>
                  <div className="list-row__body">
                    <div className="list-row__top">
                      <span className="list-row__name">{file.original_filename}</span>
                      <span className="list-row__time">{formatShortTime(file.created_at)}</span>
                    </div>
                    <div className="convo-bottom-row">
                      <span className="list-row__preview">
                        {file.uploader_name || file.uploader_id?.slice(0, 8)} &middot; {formatSize(file.size)}
                      </span>
                      {ext && <span className="file-ext-badge">{ext}</span>}
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <div className="file-expand-panel" onClick={e => e.stopPropagation()}>
                    <div className="file-expand-preview">
                      {isImage && <img src={getFileUrl(file.id)} alt={file.original_filename} className="file-expand-img" />}
                      {isVideo && <video src={getFileUrl(file.id)} controls className="file-expand-video" />}
                      {isAudio && <audio src={getFileUrl(file.id)} controls className="file-expand-audio" />}
                      {!isImage && !isVideo && !isAudio && (
                        <div className="file-expand-generic">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                          <span>{ext || 'File'}</span>
                        </div>
                      )}
                    </div>
                    <div className="file-expand-actions">
                      <a href={getFileUrl(file.id)} download={file.original_filename} className="file-expand-btn">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download
                      </a>
                      {file.message_id && (
                        <button className="file-expand-btn" onClick={() => onJumpToMessage?.(file)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                          Jump to message
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </li>
            )
          })
        )}
      </ul>
    </div>
  )
}
